import { Injectable } from '@angular/core';
import {
    FlatThemeCustomization,
    FLAT_POINT_S_CUSTOMIZATION,
    FlatThemeKey,
    FLAT_THEME_KEYS,
    validateFlatTheme
} from '../interfaces/flat-theme.interface';

// Declarar funciones globales para testing
declare global {
    interface Window {
        testFlatTheme: () => void;
        forceUpdateAssets: () => void;
        debugAssets: () => void;
        clearAssets: () => void;
    }
}

/**
 * SERVICIO DE TEMATIZACIÓN PLANA OPTIMIZADA
 * 
 * Este servicio gestiona exclusivamente la estructura de personalización plana,
 * trabajando únicamente con las 18 variables CSS que realmente se utilizan.
 * 
 * CARACTERÍSTICAS:
 * - Trabaja solo con JSON plano (sin anidación)
 * - Aplica únicamente las 18 variables CSS necesarias
 * - Validación automática de la estructura de datos
 * - Compatible con el sistema actual para migración gradual
 * - Optimizado para rendimiento y simplicidad
 * - Actualización directa de atributos src de imágenes
 */
@Injectable({
    providedIn: 'root'
})
export class FlatThemeService {
    private currentTheme: FlatThemeCustomization | null = null;
    private readonly rootElement: HTMLElement;

    constructor() {
        this.rootElement = document.documentElement;

        // Exponer funciones de testing globalmente
        if (typeof window !== 'undefined') {
            window.testFlatTheme = () => this.testCompleteAssetFlow();
            window.forceUpdateAssets = () => this.forceUpdateAssets();
            window.debugAssets = () => this.debugDirectImageUpdate();
            window.clearAssets = () => this.clearAssetVariables();
        }
    }

    applyFlatTheme(theme: FlatThemeCustomization): boolean {
        try {
            if (!validateFlatTheme(theme)) {
                return false;
            }

            FLAT_THEME_KEYS.forEach(key => {
                const value = theme[key];
                if (value) {
                    this.rootElement.style.setProperty(`--${key}`, value);
                }
            });

            this.applyAssets(theme);

            this.currentTheme = { ...theme };
            return true;

        } catch (error) {
            return false;
        }
    }

    private mapBackendToFlatTheme(backendData: any): FlatThemeCustomization {
        // Mapeo de colores
        const colorMappings = {
            'themePrimary': 'theme-primary',
            'themePrimaryHover': 'theme-primary-hover',
            'themeSecondary': 'theme-secondary',
            'themeTextPrimary': 'theme-text-primary',
            'themeBorder': 'theme-border',
            'themeTopbarBg': 'theme-topbar-bg',
            'themeProfileCardBg': 'theme-profile-card-bg',
            'themeArrowButtonBg': 'theme-arrow-button-bg',
            'themeArrowButtonHoverBg': 'theme-arrow-button-hover-bg',
            'themeArrowButtonIconColor': 'theme-arrow-button-icon-color',
            'themeSidebarActiveBg': 'theme-sidebar-active-bg',
            'themePendingOrdersBg': 'theme-pending-orders-bg',
            'themePendingOrdersBorder': 'theme-pending-orders-border',
            'themeTodayBg': 'theme-today-bg',
            'themeLinkColor': 'theme-link-color',
            'themeClientCountBg': 'theme-client-count-bg',
            'themeClientCountText': 'theme-client-count-text'
        };

        // Mapeo de assets
        const assetMappings = {
            'userAvatarUrl': 'userAvatarUrl',
            'chevronImageUrl': 'chevronImageUrl',
            'topbarLogoUrl': 'logoUrl'
        };

        // Crear tema plano
        const flatTheme: any = {};

        // Aplicar colores
        Object.keys(colorMappings).forEach(backendKey => {
            if (backendData[backendKey]) {
                const flatKey = colorMappings[backendKey as keyof typeof colorMappings];
                flatTheme[flatKey] = backendData[backendKey];
            }
        });

        // Aplicar assets
        Object.keys(assetMappings).forEach(backendKey => {
            if (backendData[backendKey]) {
                const flatKey = assetMappings[backendKey as keyof typeof assetMappings];
                flatTheme[flatKey] = backendData[backendKey];
            }
        });

        return flatTheme as FlatThemeCustomization;
    }

    /**
     * Fuerza la actualización inmediata de todos los assets del tema actual.
     * Útil para aplicar cambios inmediatos cuando las imágenes no se actualizan automáticamente.
     */
    forceUpdateAssets(): void {
        if (this.currentTheme) {
            // Re-aplicar assets del tema actual
            this.applyAssets(this.currentTheme);
        } else {
            // Si no hay tema actual, intentar actualizar con las variables CSS existentes
            this.updateDOMImages();
        }
    }

    /**
     * Aplica assets (URLs de imágenes) del tema.
     * @param theme Objeto de personalización que puede contener assets
     */
    private applyAssets(theme: FlatThemeCustomization): void {
        try {
            // Mapeo de assets internos a variables CSS
            const assetMappings = {
                userAvatarUrl: '--user-avatar-url',
                chevronImageUrl: '--chevron-image-url',
                logoUrl: '--logo-url'
            };

            let hasValidAssets = false;

            // Aplicar cada asset si está presente y es válido
            Object.entries(assetMappings).forEach(([assetKey, cssVar]) => {
                const assetValue = (theme as any)[assetKey];
                if (assetValue && this.isValidAssetUrl(assetValue)) {
                    // Limpiar valor (remover comillas extra, espacios)
                    const cleanValue = assetValue.replace(/^["']|["']$/g, '').trim();

                    // Formatear como URL para CSS
                    const cssValue = cleanValue.startsWith('url(') ? cleanValue : `url(${cleanValue})`;

                    this.rootElement.style.setProperty(cssVar, cssValue);
                    hasValidAssets = true;
                }
            });

            // Solo notificar cambios si hay assets válidos
            if (hasValidAssets) {
                this.notifyAssetUpdater();
            }

        } catch (error) {
            console.error('FlatThemeService: Error applying assets:', error);
        }
    }

    /**
     * Valida que una URL de asset sea válida
     */
    private isValidAssetUrl(url: string): boolean {
        return !!(url &&
            typeof url === 'string' &&
            url.trim() !== '' &&
            !url.includes('[object') &&
            url !== 'none' &&
            url !== 'initial' &&
            url !== 'inherit' &&
            url !== 'unset' &&
            url !== 'undefined' &&
            url !== 'null');
    }

    /**
     * Notifica al AssetUpdaterService para que actualice las imágenes
     */
    private notifyAssetUpdater(): void {
        // Usar un evento personalizado para notificar cambios
        setTimeout(() => {
            const event = new CustomEvent('flatThemeAssetsUpdated', {
                detail: {
                    userAvatarUrl: this.getCSSVariableCleanValue('--user-avatar-url'),
                    chevronImageUrl: this.getCSSVariableCleanValue('--chevron-image-url'),
                    logoUrl: this.getCSSVariableCleanValue('--logo-url')
                }
            });
            document.dispatchEvent(event);

            // También actualizar directamente en el DOM para aplicación inmediata
            this.updateDOMImages();
        }, 10);
    }

    /**
     * Obtiene el valor limpio de una variable CSS (sin url() wrapper)
     */
    private getCSSVariableCleanValue(cssVariable: string): string | null {
        try {
            const value = getComputedStyle(this.rootElement).getPropertyValue(cssVariable)?.trim();

            // Verificar que el valor es válido
            if (!value ||
                value === '' ||
                value === 'none' ||
                value === 'initial' ||
                value === 'inherit' ||
                value === 'unset' ||
                value.includes('[object')) {
                return null;
            }

            // Remover url() wrapper y comillas
            const cleanValue = value.replace(/^url\(['"]?|['"]?\)$/g, '').trim();

            // Verificar que después de limpiar sigue siendo válido
            if (!cleanValue || cleanValue === '') {
                return null;
            }

            return cleanValue;
        } catch (error) {
            console.warn('Error getting CSS variable clean value:', error);
            return null;
        }
    }

    /**
     * Actualiza directamente las imágenes en el DOM
     * Este método cambia los atributos 'src' de las imágenes de forma inmediata
     */
    private updateDOMImages(): void {
        try {
            const avatarUrl = this.getCSSVariableCleanValue('--user-avatar-url');
            const chevronUrl = this.getCSSVariableCleanValue('--chevron-image-url');
            const logoUrl = this.getCSSVariableCleanValue('--logo-url');

            // Actualizar avatares - buscar por múltiples selectores
            if (avatarUrl) {
                const avatarSelectors = [
                    'img[alt="User Avatar"]',
                    '.profile-avatar img',
                    'img[src*="nav-user-icon"]',
                    'img[src*="user-avatar"]'
                ];

                avatarSelectors.forEach(selector => {
                    const avatarImages = document.querySelectorAll(selector);
                    avatarImages.forEach((img: Element) => {
                        const imgElement = img as HTMLImageElement;
                        if (imgElement.src) {
                            imgElement.src = avatarUrl;
                        }
                    });
                });
            }

            // Actualizar chevron - buscar por múltiples selectores
            if (chevronUrl) {
                const chevronSelectors = [
                    '.button-arrow img',
                    'img[src*="chevron"]',
                    '.topbar img[src*="chevron"]'
                ];

                chevronSelectors.forEach(selector => {
                    const chevronImages = document.querySelectorAll(selector);
                    chevronImages.forEach((img: Element) => {
                        const imgElement = img as HTMLImageElement;
                        if (imgElement.src) {
                            imgElement.src = chevronUrl;
                        }
                    });
                });
            }

            // Actualizar logo (background-image)
            if (logoUrl) {
                const logoSelectors = [
                    '.logo',
                    '.topbar .logo',
                    '[class*="logo"]'
                ];

                logoSelectors.forEach(selector => {
                    const logoElements = document.querySelectorAll(selector);
                    logoElements.forEach((logo: Element) => {
                        const logoElement = logo as HTMLElement;
                        // Solo aplicar si el elemento existe y no tiene contenido de imagen
                        if (logoElement && !logoElement.querySelector('img')) {
                            logoElement.style.backgroundImage = `url(${logoUrl})`;
                            logoElement.style.backgroundSize = 'contain';
                            logoElement.style.backgroundRepeat = 'no-repeat';
                            logoElement.style.backgroundPosition = 'center';
                        }
                    });
                });
            }
        } catch (error) {
            console.error('FlatTheme: Error actualizando imágenes en DOM:', error);
        }
    }

    /**
     * MÉTODO DE DEBUG: Actualiza directamente las imágenes en el DOM
     */
    debugDirectImageUpdate(): void {
        this.updateDOMImages();
    }

    /**
     * MÉTODO DE PRUEBA COMPLETO: Simula la recepción de datos del backend y los aplica
     * Puedes ejecutar este método desde la consola del navegador para probar todo el flujo
     */
    testCompleteAssetFlow(): void {
        // 1. Simular datos del backend (formato real)
        const backendData = {
            "themePrimary": "#0066CC",
            "themePrimaryHover": "#0052A3",
            "themeSecondary": "#00BD3F",
            "themeTextPrimary": "#213BA7",
            "themeBorder": "#00BD3F",
            "themeTopbarBg": "#213BA7",
            "themeProfileCardBg": "#FFFFFF",
            "themeArrowButtonBg": "#00BD3F",
            "themeArrowButtonHoverBg": "#009933",
            "themeSidebarActiveBg": "#CCD3EF",
            "themePendingOrdersBg": "#CCD3EF",
            "themePendingOrdersBorder": "none",
            "themeTodayBg": "#CCD3EF",
            "themeLinkColor": "#00BD3F",
            "themeClientCountBg": "#CFE6AA",
            "themeClientCountText": "#213BA7",
            "themeArrowButtonIconColor": "#213BA7",
            "userAvatarUrl": "assets/pointS/picto-s-circle.svg",
            "chevronImageUrl": "assets/icons/chevron-left-blue.svg",
            "topbarLogoUrl": "assets/pointS/logo.webp"
        };

        // 2. Mapear a formato plano
        const flatTheme = this.mapBackendToFlatTheme(backendData);

        // 3. Aplicar tema
        const success = this.applyFlatTheme(flatTheme);
    }

    /**
     * Limpia las variables CSS de assets cuando no hay tema aplicado
     */
    clearAssetVariables(): void {
        try {
            const assetVariables = ['--user-avatar-url', '--chevron-image-url', '--logo-url'];

            assetVariables.forEach(cssVar => {
                this.rootElement.style.removeProperty(cssVar);
            });
        } catch (error) {
            console.error('Error limpiando variables de assets:', error);
        }
    }
}
